import type { AxiosInstance } from 'axios';

import type { auth, users } from '@terra/contracts';

/** Auth endpoint group. Types come straight from `@terra/contracts` so FE/BE never drift. */
export function authEndpoints(http: AxiosInstance) {
  return {
    /**
     * Start a sign-up. Creates **no** account — it holds the details and texts a
     * 6-digit code to `phone`. Follow with `verifyRegistration` to get a session.
     */
    register: (body: auth.RegisterInput) =>
      http.post<auth.RegisterStartResponse>('/auth/register', body).then((r) => r.data),

    /** Start a seller sign-up. Same two-phase contract as `register`. */
    registerVendor: (body: auth.RegisterVendorRequest) =>
      http.post<auth.RegisterStartResponse>('/auth/vendor/register', body).then((r) => r.data),

    /** Another code for a sign-up already in progress. */
    resendRegistrationOtp: (body: auth.RegisterResendRequest) =>
      http.post<auth.RegisterStartResponse>('/auth/register/resend', body).then((r) => r.data),

    /** Seller app sign-up, step 1: personal details only; texts a code to `phone`. */
    vendorRegisterStart: (body: auth.VendorRegisterStartRequest) =>
      http
        .post<auth.RegisterStartResponse>('/auth/vendor/register/start', body)
        .then((r) => r.data),

    /** Step 2: the code proves the phone and returns a registration token. No account yet. */
    vendorRegisterVerifyPhone: (body: auth.VendorRegisterVerifyPhoneRequest) =>
      http
        .post<auth.VendorRegisterVerifyPhoneResponse>('/auth/vendor/register/verify-phone', body)
        .then((r) => r.data),

    /** Step 3: business details + the token create the seller and return the session. */
    vendorRegisterComplete: (body: auth.VendorRegisterCompleteRequest) =>
      http.post<auth.AuthResponse>('/auth/vendor/register/complete', body).then((r) => r.data),

    /** Finish a sign-up: the code creates the account and returns the session. */
    verifyRegistration: (body: auth.RegisterVerifyRequest) =>
      http.post<auth.AuthResponse>('/auth/register/verify', body).then((r) => r.data),

    login: (body: auth.LoginRequest) =>
      http.post<auth.AuthResponse>('/auth/login', body).then((r) => r.data),

    /** Admin panel step 1: checks the password; on success texts a code to the admin's mobile. No session yet. */
    adminLoginStart: (body: auth.AdminLoginStartRequest) =>
      http.post<auth.AdminLoginStartResponse>('/auth/admin/login/start', body).then((r) => r.data),

    /** Admin panel step 2: the challenge token from step 1 plus the SMS code → session. */
    adminLoginVerify: (body: auth.AdminLoginVerifyRequest) =>
      http.post<auth.AuthResponse>('/auth/admin/login/verify', body).then((r) => r.data),

    /** Seller portal/app step 1: checks the password; on success texts a code to the seller's mobile. No session yet. */
    vendorLoginStart: (body: auth.VendorLoginStartRequest) =>
      http
        .post<auth.VendorLoginStartResponse>('/auth/vendor/login/start', body)
        .then((r) => r.data),

    /** Seller portal/app step 2: the challenge token from step 1 plus the SMS code → session. */
    vendorLoginVerify: (body: auth.VendorLoginVerifyRequest) =>
      http.post<auth.AuthResponse>('/auth/vendor/login/verify', body).then((r) => r.data),

    requestOtp: (body: auth.OtpRequestRequest) =>
      http.post<{ sent: true; expiresIn: number }>('/auth/otp/request', body).then((r) => r.data),

    verifyOtp: (body: auth.OtpVerifyRequest) =>
      http.post<auth.AuthResponse>('/auth/otp/verify', body).then((r) => r.data),

    refresh: (body?: auth.RefreshRequest) =>
      http.post<auth.AuthResponse>('/auth/refresh', body ?? {}).then((r) => r.data),

    logout: () => http.post<void>('/auth/logout').then(() => undefined),

    // ── account security (signed-in user) ──
    changePassword: (body: users.ChangePasswordRequest) =>
      http
        .post<{ data: { otherSessionsRevoked: number } }>('/auth/account/password', body)
        .then((r) => r.data.data),
    startContactChange: (body: users.StartContactChangeRequest) =>
      http
        .post<{ data: users.StartContactChangeResponse }>('/auth/account/contact/start', body)
        .then((r) => r.data.data),
    verifyContactChange: (body: users.VerifyContactChangeRequest) =>
      http
        .post<{ data: { email: string; phone: string } }>('/auth/account/contact/verify', body)
        .then((r) => r.data.data),
    listSessions: () =>
      http.get<{ data: users.SessionSummary[] }>('/auth/account/sessions').then((r) => r.data.data),
    revokeOtherSessions: (currentPassword: string) =>
      http
        .post<{ data: { revoked: number } }>('/auth/account/sessions/revoke-others', {
          currentPassword,
        })
        .then((r) => r.data.data),

    /** Start a reset. `channel` picks where the code is sent. */
    forgotPassword: (body: auth.ForgotPasswordInput) =>
      http.post<auth.ForgotPasswordResponse>('/auth/password/forgot', body).then((r) => r.data),

    /** Check a reset code (without spending it) before asking for the new password. */
    verifyResetOtp: (body: auth.VerifyResetOtpRequest) =>
      http.post<{ valid: true }>('/auth/password/verify-otp', body).then((r) => r.data),

    /** Finish an OTP-based reset: code + new password. */
    resetPasswordWithOtp: (body: auth.ResetPasswordWithOtpRequest) =>
      http.post<{ success: true }>('/auth/password/reset-otp', body).then((r) => r.data),
  };
}

export type AuthEndpoints = ReturnType<typeof authEndpoints>;
